import { ParameterService } from 'projects/lib-shared-core/src/public-api';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { SvcDataPrefetching, SvcDataPrefetchingItem, SvcDataPrefetchingItemStructure } from './svc-data-prefetching.abstract';
import { map, of, tap, zip } from 'rxjs';
import { SvcAppDataPrefetching as SvcAppDataPrefetching } from './svc-app-data-prefetching.abstract';
import { UserService } from '../features/user/services/user.service';
import { AuthService } from '../auth/auth.service';
import { SvcParameters } from '../enums/svc-parameter.enum';
import { SvcParamItem } from '../services/parameter/svc-param-item.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface SvcGlobalData extends SvcDataPrefetchingItemStructure {
  isGamefied: SvcDataPrefetchingItem<boolean>;
  voiceToText: SvcDataPrefetchingItem<boolean>;
  bosShowTabActionPlan: SvcDataPrefetchingItem<boolean>;
  unsafeConditionShowTabActionPlan: SvcDataPrefetchingItem<boolean>;
  unsafeConditionShowTabPlanning: SvcDataPrefetchingItem<boolean>;
  unsafeConditionShowIntegrationCmms: SvcDataPrefetchingItem<boolean>;
  unsafeConditionTeamFieldIsActive: SvcDataPrefetchingItem<boolean>;
  unsafeConditionTeamFieldIsRequired: SvcDataPrefetchingItem<boolean>;
  unsafeConditionEquipmentFieldIsActive: SvcDataPrefetchingItem<boolean>;
  unsafeConditionEquipmentFieldIsRequired: SvcDataPrefetchingItem<boolean>;
  bosShowOriginator: SvcDataPrefetchingItem<boolean>;
  alertShowTabActionPlan: SvcDataPrefetchingItem<boolean>;
  workCouncils: SvcDataPrefetchingItem<boolean>;
  subtitleEnabled: SvcDataPrefetchingItem<boolean>;
  bosTeamFieldIsActive: SvcDataPrefetchingItem<boolean>;
  bosTeamFieldIsRequired: SvcDataPrefetchingItem<boolean>;
  defectTagShowTabActionPlan: SvcDataPrefetchingItem<boolean>;
  defectTagShowPlanedIsVisible: SvcDataPrefetchingItem<boolean>;
  defectTagShowPlanedIsRequired: SvcDataPrefetchingItem<boolean>;
  defectTagResponsibleTeamIsVisible: SvcDataPrefetchingItem<boolean>;
  defectTagResponsibleTeamIsRequired: SvcDataPrefetchingItem<boolean>;
  defectTagShowIntegrationCmms: SvcDataPrefetchingItem<boolean>;
  enableLoginMultiUsers: SvcDataPrefetchingItem<boolean>;
  /** Admin > Funcionalidades > "Mapa": whether the map feature is enabled for the site (AB#71830). */
  enableMaps: SvcDataPrefetchingItem<boolean>;
  /** CIL "Edição de inspeções concluídas - Revamp": TRUE = only the module admin may edit (AB#71835). */
  cilRestrictEditCompletedInspections: SvcDataPrefetchingItem<boolean>;
  /** CIL "Restringe novo registro pai - Revamp": TRUE = only the module admin may create/edit a PLAN (AB#69738). */
  cilRestrictNewParentRecord: SvcDataPrefetchingItem<boolean>;
}

@Injectable({
  providedIn: 'root',
})
export class SvcGlobalDataPrefetching extends SvcDataPrefetching<SvcGlobalData> {
  private appDataPrefetching = inject(SvcAppDataPrefetching, { optional: true });
  private authService = inject(AuthService);
  private alreadyFetched = false;

  private userService = inject(UserService);
  private svcParameterService = inject(ParameterService);

  #destroyRef = inject(DestroyRef);

  public isLoading$ = zip(this._isLoading.asObservable(), this.appDataPrefetching?.isLoading$ ?? of(false)).pipe(map((result) => result[0] && result[1]));

  public getAppData<T extends SvcDataPrefetchingItemStructure>(): T {
    return <T>this.appDataPrefetching?.data;
  }

  constructor() {
    super();
    this.#initializeFromAuth();
  }

  private getPrepareDataFetching(): SvcGlobalData {
    return {
      isGamefied: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.userService.isGamified$,
      }),
      bosShowTabActionPlan: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.BosShowTabActionPlan).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      bosShowOriginator: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.BosFieldOriginatorModal).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      voiceToText: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.VoiceToText).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      alertShowTabActionPlan: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.AlertShowTabActionPlan).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      unsafeConditionShowTabActionPlan: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.UnsafeConditionShowTabActionPlan).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      unsafeConditionShowTabPlanning: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.UnsafeConditionShowTabPlanning).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      workCouncils: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.WorkCouncils).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      unsafeConditionShowIntegrationCmms: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.UnsafeConditionShowIntegrationCmms).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      unsafeConditionTeamFieldIsActive: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () =>
          this.svcParameterService
            .getSiteParamAsObject(SvcParameters.UnsafeConditionRevampResponsibleTeam)
            .pipe(map((response) => ['TRUE', '1'].includes(response?.value?.toUpperCase()))),
      }),
      unsafeConditionTeamFieldIsRequired: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () =>
          this.svcParameterService
            .getSiteParamAsObject(SvcParameters.UnsafeConditionRevampResponsibleTeam)
            .pipe(map((response) => !!response?.mandatory)),
      }),
      unsafeConditionEquipmentFieldIsActive: new SvcDataPrefetchingItem<boolean>({
        valueFallback: true,
        get: () =>
          this.svcParameterService
            .getSiteParamAsObject(SvcParameters.UnsafeConditionRevampEquipment)
            .pipe(map((response) => (response?.value == null ? true : ['TRUE', '1'].includes(response.value.toUpperCase())))),
      }),
      unsafeConditionEquipmentFieldIsRequired: new SvcDataPrefetchingItem<boolean>({
        valueFallback: true,
        get: () =>
          this.svcParameterService
            .getSiteParamAsObject(SvcParameters.UnsafeConditionRevampEquipment)
            .pipe(map((response) => response?.mandatory ?? true)),
      }),
      subtitleEnabled: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.SubtitleEnabled).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      bosTeamFieldIsActive: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () =>
          this.svcParameterService
            .getSiteParamAsObject(SvcParameters.BosRevampResponsibleTeam)
            .pipe(map((response) => ['TRUE', '1'].includes(response?.value?.toUpperCase()))),
      }),
      bosTeamFieldIsRequired: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () =>
          this.svcParameterService
            .getSiteParamAsObject(SvcParameters.BosRevampResponsibleTeam)
            .pipe(map((response) => !!response?.mandatory)),
      }),
      enableLoginMultiUsers: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.EnableLoginMultiUsers).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      defectTagShowTabActionPlan: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.DefectTagShowTabActionPlan).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      defectTagShowPlanedIsVisible: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.DefectTagShowTabPlanning).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      defectTagShowPlanedIsRequired: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParamAsObject(SvcParameters.DefectTagShowTabPlanning).pipe(map((response) => ['TRUE', '1'].includes(response?.value?.toUpperCase()) && (response?.mandatory ?? false))),
      }),
      defectTagResponsibleTeamIsVisible: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.DefectTagRevampResponsibleTeam).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      defectTagResponsibleTeamIsRequired: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParamAsObject(SvcParameters.DefectTagRevampResponsibleTeam).pipe(map((response) => ['TRUE', '1'].includes(response?.value?.toUpperCase()) && (response?.mandatory ?? false))),
      }),
      defectTagShowIntegrationCmms: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.DefectTagShowIntegrationCmms).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      // AB#71830 — site-wide "Mapa" feature switch. Falls back to false so an unreadable parameter
      // hides the map instead of exposing a feature the site turned off.
      enableMaps: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.EnableMaps).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      // AB#71835 — CIL "Edição de inspeções concluídas - Revamp". Falls back to false (unrestricted),
      // matching the backend seed for sites that never had the legacy rule.
      cilRestrictEditCompletedInspections: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.CilRestrictEditCompletedInspections).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
      // AB#69738 — CIL "Restringe novo registro pai - Revamp"; same seed default (unrestricted).
      cilRestrictNewParentRecord: new SvcDataPrefetchingItem<boolean>({
        valueFallback: false,
        get: () => this.svcParameterService.getSiteParam(SvcParameters.CilRestrictNewParentRecord).pipe(map((response) => response?.toUpperCase() === 'TRUE')),
      }),
    };
  }

  public initAndFetch(options?: { force: boolean }): void {
    this.authService.check().subscribe((authenticated) => {
      if (authenticated && (!this.alreadyFetched || options?.force)) {
        this.alreadyFetched = true;
        this.reset();
        this.prepareDataFetching(this.getPrepareDataFetching());
        this.fetch();
        this.appDataPrefetching?.initAndFetch();
      }
    });
  }

  public reset(): void {
    super.reset();
    this.appDataPrefetching?.reset();
  }

  #initializeFromAuth(): void {
    [this.authService.signIn$, this.authService.signOut$].forEach((observable$) => {
      observable$
        .pipe(
          tap(() => {
            this.alreadyFetched = false;
            this.reset();
          }),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe();
    });
  }
}
